/**
 * Session-runs API (M-X6) — companion to session-chat.ts.
 *
 * Lets the chat pane reattach to in-flight runs after a reload and
 * interrupt an active stream so the user can immediately type a new
 * follow-up.
 *
 * Two surfaces:
 *   1. listActive(sessionId) — REST GET, returns the runs row(s) where
 *      status IN ('starting','running').
 *   2. stream(runId) — SSE async generator, same event shape as
 *      sessionChatApi.stream so the existing reducer just keeps
 *      working. The generator finishes on `done` event or socket close.
 *   3. interrupt(runId) — REST POST that docker-stops the run and marks
 *      it `interrupted`.
 */
import { apiFetch, apiJson } from './client';
import type { ChatStreamEvent } from './session-chat';

export interface SessionRunInfo {
  id: string;
  status: 'starting' | 'running' | 'done' | 'failed' | 'interrupted' | 'timeout';
  startedAt: string;
  lastHeartbeatAt: string;
  model: string | null;
  effort: string | null;
  permissionMode: string | null;
  userMessageId: string | null;
  /** Who started this run (migration 0414 UI: "Started by <person>"); null for a system run. */
  initiatedByUserId?: string | null;
  initiatedByName?: string | null;
  // ── Durable delivery ledger (0130/0133/0134), null on a pre-0133 box ──
  //
  // FD-B / SPEC-00 §6 LD-15: a prompt sent into a busy card is ACCEPTED and
  // QUEUED, and the queue is visible to everyone on the card. The gateway has
  // returned these fields
  // on this route since the ledger shipped; until now nothing in the UI read
  // them, so the queue a person could see was `chat-queue.ts` — one browser's
  // localStorage — and a second participant on the card saw the prompt's text
  // with no way to tell waiting from ignored.
  //
  // Read from THIS route deliberately: it is card-scoped and already exists.
  // LD-15 forbids opening a second, cross-account read path for delivery state
  // (FD-A / A2.9 / A2.10 / the LD-3 account fence are untouched by it).
  /** 'pending' | 'delivering' | 'delivered' | 'undeliverable'. */
  deliveryState?: string | null;
  deliveryAttempts?: number | null;
  deliveryDeferrals?: number | null;
  lastDeferredReason?: string | null;
  lastDeliveryError?: string | null;
  deliveredAt?: string | null;
  /**
   * The ledger's own ordering sequence — a bigint, so it arrives as a string.
   * The queue is a LINE; this is what says which place a prompt holds. Never
   * coerce it to a JS number.
   */
  deliverySeq?: string | null;
  /** M-X11: Live Activity hydration after reattach. */
  vendor?: 'anthropic' | 'openai' | 'google' | null;
  containerName?: string | null;
  tokensInput?: number;
  tokensOutput?: number;
  tokensCacheRead?: number;
  tokensCacheCreation?: number;
  toolCallsCount?: number;
  subagentsSpawned?: number;
}

export interface InterruptResult {
  status: 'interrupted';
  stopped_at: string;
  run_id?: string;
}

/**
 * The run exists, but this deployment cannot expose a safe read-only cursor.
 * This is not a failed turn and never authorizes a resend.
 */
export class ReattachUnavailableError extends Error {
  readonly code = 'run_not_streamable';
  constructor(
    readonly reason: string,
    readonly retryable = false,
  ) {
    super(reason);
    this.name = 'ReattachUnavailableError';
  }
}

export const sessionRunsApi = {
  listActive(sessionId: string): Promise<SessionRunInfo[]> {
    return apiJson<{ runs: SessionRunInfo[] }>(
      `/sessions/${encodeURIComponent(sessionId)}/runs/active`,
    ).then((r) => r.runs);
  },

  interrupt(runId: string, principalFence: string): Promise<InterruptResult> {
    return apiJson<InterruptResult>(`/runs/${encodeURIComponent(runId)}/interrupt`, {
      method: 'POST',
      json: {},
      headers: { 'X-Chaos-Expected-Principal': principalFence },
    });
  },

  /**
   * Reattach to a live (or cached) run. Same event shape as
   * sessionChatApi.stream. Caller consumes with for-await.
   */
  async *stream(
    runId: string,
    signal?: AbortSignal,
  ): AsyncGenerator<ChatStreamEvent, void, void> {
    const resp = await apiFetch(`/runs/${encodeURIComponent(runId)}/stream`, {
      method: 'GET',
      headers: { Accept: 'text/event-stream' },
      signal,
    });
    if (!resp.ok) {
      let parsed: { error?: string; message?: string; reason?: string } = {};
      try {
        parsed = await resp.json();
      } catch {
        /* noop */
      }
      if (
        (resp.status === 409 && parsed.error === 'run_not_streamable') ||
        (resp.status === 429 && parsed.error === 'reattach_capacity')
      ) {
        throw new ReattachUnavailableError(
          parsed.reason ?? parsed.message ?? 'cursor_unavailable',
          resp.status === 429,
        );
      }
      // No other HTTP status is a terminal fact about the run. Throw it into
      // SessionChatPane's capped reconciliation path; synthesising `done`
      // here clears the active run and immediately remounts this same GET.
      throw new Error(parsed.message ?? parsed.error ?? `http_${resp.status}`);
    }
    if (!resp.body) {
      // A successful status without a readable transport is equally
      // non-authoritative: no SSE terminal record was observed.
      throw new Error('no_response_body');
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let currentEvent: string | null = null;
    let currentData: string[] = [];
    let sawDone = false;
    let unavailableReason: string | null = null;

    const drain = (): ChatStreamEvent[] => {
      if (!currentEvent || currentData.length === 0) return [];
      const evtName = currentEvent;
      const raw = currentData.join('\n');
      currentEvent = null;
      currentData = [];
      let parsed: any;
      try {
        parsed = JSON.parse(raw);
      } catch {
        return [{ kind: 'error', message: `bad_sse_payload: ${raw.slice(0, 200)}` }];
      }
      switch (evtName) {
        case 'text':
          return [{ kind: 'text', delta: String(parsed.delta ?? '') }];
        case 'thinking':
          return [{ kind: 'thinking', delta: String(parsed.delta ?? '') }];
        case 'tool_use':
          return [
            {
              kind: 'tool_use',
              id: String(parsed.id),
              name: String(parsed.name),
              input: parsed.input,
            },
          ];
        case 'tool_result':
          return [
            {
              kind: 'tool_result',
              tool_use_id: String(parsed.tool_use_id),
              ok: !!parsed.ok,
              status: Number(parsed.status ?? 0),
              json: parsed.json,
            },
          ];
        case 'error':
          return [{ kind: 'error', message: String(parsed.message ?? 'error') }];
        case 'done':
          sawDone = true;
          return [
            {
              kind: 'done',
              user_msg_id: parsed.user_msg_id ?? null,
              agent_msg_id: parsed.agent_msg_id ?? null,
            },
          ];
        case 'reattach_ready':
          return [{ kind: 'reattach_ready', cursor: 'attached' }];
        case 'reattach_unavailable':
          unavailableReason = String(parsed.reason ?? 'cursor_unavailable');
          return [
            {
              kind: 'reattach_unavailable',
              reason: String(parsed.reason ?? 'cursor_unavailable'),
            },
          ];
        // M-X11: Live Activity events — reuse the same kinds as the
        // chat POST stream so the UI's reducer doesn't have to branch.
        // The reattach SSE today only re-emits text/tool_use/tool_result
        // via container logs replay, so progress / usage / subagent
        // events are not surfaced here. The UI hydrates those counters
        // from listActive() at reconnect.
        case 'run_started':
          return [
            {
              kind: 'run_started',
              run_id: String(parsed.run_id ?? ''),
              container: String(parsed.container ?? ''),
              user_msg_id:
                typeof parsed.user_msg_id === 'string' ? parsed.user_msg_id : null,
            },
          ];
        case 'run_progress':
          return [
            {
              kind: 'run_progress',
              run_id: String(parsed.run_id ?? ''),
              elapsedMs: Number(parsed.elapsedMs ?? 0),
              tokensInput: Number(parsed.tokensInput ?? 0),
              tokensOutput: Number(parsed.tokensOutput ?? 0),
              tokensCacheRead: Number(parsed.tokensCacheRead ?? 0),
              tokensCacheCreation: Number(parsed.tokensCacheCreation ?? 0),
              contextTokens:
                typeof parsed.contextTokens === 'number' ? parsed.contextTokens : undefined,
              subAgentsActive: Number(parsed.subAgentsActive ?? 0),
              subAgentsCompleted: Number(parsed.subAgentsCompleted ?? 0),
              toolCallsCount: Number(parsed.toolCallsCount ?? 0),
              currentActivity: String(parsed.currentActivity ?? ''),
            },
          ];
        case 'tool_use_started':
          return [
            {
              kind: 'tool_use_started',
              id: String(parsed.id ?? ''),
              name: String(parsed.name ?? 'unknown'),
              input_preview: String(parsed.input_preview ?? ''),
            },
          ];
        case 'tool_use_completed':
          return [
            {
              kind: 'tool_use_completed',
              tool_use_id: String(parsed.tool_use_id ?? ''),
              ok: !!parsed.ok,
            },
          ];
        case 'subagent_started':
          return [
            {
              kind: 'subagent_started',
              parent_tool_use_id: String(parsed.parent_tool_use_id ?? ''),
              subagent_type:
                typeof parsed.subagent_type === 'string' ? parsed.subagent_type : null,
              description:
                typeof parsed.description === 'string' ? parsed.description : null,
            },
          ];
        case 'subagent_completed':
          return [
            {
              kind: 'subagent_completed',
              parent_tool_use_id: String(parsed.parent_tool_use_id ?? ''),
              ok: !!parsed.ok,
            },
          ];
        case 'usage':
          return [
            {
              kind: 'usage',
              inputTokens:
                typeof parsed.inputTokens === 'number' ? parsed.inputTokens : undefined,
              outputTokens:
                typeof parsed.outputTokens === 'number' ? parsed.outputTokens : undefined,
              cacheReadTokens:
                typeof parsed.cacheReadTokens === 'number' ? parsed.cacheReadTokens : undefined,
              cacheCreationTokens:
                typeof parsed.cacheCreationTokens === 'number'
                  ? parsed.cacheCreationTokens
                  : undefined,
              // Deliberately NOT defaulted to 0: absent means "not measured",
              // and a 0 here would render as an empty context window.
              contextTokens:
                typeof parsed.contextTokens === 'number' ? parsed.contextTokens : undefined,
            },
          ];
        default:
          return [];
      }
    };

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        const trimmed = line.replace(/\r$/, '');
        if (trimmed === '') {
          for (const evt of drain()) yield evt;
          continue;
        }
        if (trimmed.startsWith(':')) continue;
        if (trimmed.startsWith('event:')) {
          currentEvent = trimmed.slice(6).trim();
        } else if (trimmed.startsWith('data:')) {
          currentData.push(trimmed.slice(5).replace(/^ /, ''));
        }
      }
    }
    if (buffer) {
      const trimmed = buffer.replace(/\r$/, '');
      if (trimmed.startsWith('event:')) currentEvent = trimmed.slice(6).trim();
      else if (trimmed.startsWith('data:'))
        currentData.push(trimmed.slice(5).replace(/^ /, ''));
    }
    for (const evt of drain()) yield evt;
    if (!signal?.aborted && unavailableReason) {
      throw new ReattachUnavailableError(unavailableReason);
    }
    if (!signal?.aborted && !sawDone) {
      throw new Error('reattach_stream_ended_without_done');
    }
  },
};

export const sessionRunsKeys = {
  all: ['session-runs'] as const,
  active: (sessionId: string) => [...sessionRunsKeys.all, 'active', sessionId] as const,
};
